// Room view of the type summary: every operation and its contribution per room.
// Nothing is added up here: different operations and units are only listed, and an
// unknown contribution stays null.

export type SummaryContribution = { floor?: unknown; place?: unknown; value?: unknown; unit?: unknown; work_id?: unknown };
export type SummaryOperation = {
  description: string; mount: string; unit: string; unknown: number;
  work_ids: string[]; contributions: SummaryContribution[];
};
export type SummaryGroup = {
  building: string; discipline: string; category: string; group: string; operations: SummaryOperation[];
};
export type SummaryFilter = { building?: string; section?: string; category?: string; type?: string };

export type RoomOperation = {
  discipline: string; category: string; group: string; description: string; mount: string;
  value: string | null; unit: string; workId: string;
};
export type RoomRow = { building: string; floor: string; place: string; operations: RoomOperation[] };

const byName = (a: string, b: string) => a.localeCompare(b, "ru", { numeric: true });
export const UNASSIGNED = "__unassigned";

export function floorLabel(value: unknown): string {
  const floor = String(value ?? "").trim();
  return !floor || /этаж/iu.test(floor) ? floor : `этаж ${floor}`;
}

export function placementLabels(items: readonly SummaryContribution[]): string[] {
  const places = items.map(item => ({ floor: floorLabel(item.floor), place: String(item.place ?? "").trim() }));
  const named = new Set(places.flatMap(item => {
    const match = item.place.match(/^(-?\d+(?:\.\d+)?[a-zа-я]?)(?:\s*[,;]\s*|\s+[—–-]\s+)\S/iu);
    return match ? [JSON.stringify([item.floor, match[1]])] : [];
  }));
  // Hide only a bare number already shown with a description on this floor.
  // Differing notes remain visible; these labels never merge contributions or quantities.
  return [...new Set(places
    .filter(item => !named.has(JSON.stringify([item.floor, item.place])))
    .map(item => [item.floor, item.place].filter(Boolean).join(", "))
    .filter(Boolean))];
}

export function filterGroups<T extends SummaryGroup>(groups: readonly T[], filter: SummaryFilter): T[] {
  return groups.filter(g =>
    (!filter.building || (g.building || UNASSIGNED) === filter.building) &&
    (!filter.section || g.discipline === filter.section) &&
    (!filter.category || g.category === filter.category) &&
    (!filter.type || g.group === filter.type));
}

/** Rooms of different buildings with the same number stay different rooms. */
export function roomSummary(groups: readonly SummaryGroup[]): RoomRow[] {
  const rooms = new Map<string, RoomRow>();
  for (const group of groups) for (const operation of group.operations) for (const item of operation.contributions) {
    const floor = String(item.floor ?? ""), place = String(item.place ?? "");
    if (!place) continue;
    const key = JSON.stringify([group.building, floor, place]);
    const room = rooms.get(key) ?? { building: group.building, floor, place, operations: [] };
    rooms.set(key, room);
    room.operations.push({
      discipline: group.discipline, category: group.category, group: group.group,
      description: operation.description, mount: operation.mount,
      value: item.value == null ? null : String(item.value),
      unit: String(item.unit ?? operation.unit), workId: String(item.work_id ?? operation.work_ids[0] ?? ""),
    });
  }
  return [...rooms.values()].sort((a, b) => byName(a.building, b.building) || byName(a.floor, b.floor) || byName(a.place, b.place));
}

export type ScopeCounter = { building: string; discipline: string; works: number; unknown: number; withoutRooms: number };

/** How many works a building and section hold, and how many of them still lack a quantity or rooms. */
export function scopeCounters(groups: readonly SummaryGroup[]): ScopeCounter[] {
  const counters = new Map<string, ScopeCounter>();
  for (const group of groups) {
    const key = JSON.stringify([group.building, group.discipline]);
    const counter = counters.get(key) ?? { building: group.building, discipline: group.discipline, works: 0, unknown: 0, withoutRooms: 0 };
    counters.set(key, counter);
    for (const operation of group.operations) {
      counter.works += operation.work_ids.length;
      counter.unknown += operation.unknown;
      if (!operation.contributions.length) counter.withoutRooms += operation.work_ids.length;
    }
  }
  return [...counters.values()].sort((a, b) => byName(a.building, b.building) || byName(a.discipline, b.discipline));
}
